<script setup>
import { vMaska } from "maska/vue";
</script>

<template>
  <div class="financial-transactions-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-file-invoice-dollar"></i>
        Lançamentos Financeiros
      </h1>
      <p class="page-subtitle">Gerencie os lançamentos financeiros dos vendedores</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          v-if="canCreateEditTransactions"
          class="tab-button" 
          :class="{ active: selectedTab === 2 }" 
          @click="financialTransactions.id ? selectedTab = 2 : createFinancialTransactions()"
        >
          <i class="fa-solid fa-plus"></i>
          {{ financialTransactions.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <!-- Filtros -->
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros de Consulta
          </h3>
        </div>
        
        <div class="filters-content">
          <div class="filters-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-building"></i>
                Apenas Lançamento da Banca
              </label>
              <div class="checkbox-container">
                <input 
                  class="form-checkbox" 
                  type="checkbox" 
                  id="clearSeller" 
                  v-model="clearSellerSearch" 
                />
                <label class="checkbox-label" for="clearSeller">
                  Mostrar apenas lançamentos da banca
                </label>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-hand-holding-dollar"></i>
                Cobrador
              </label>
              <div class="searchable-select-wrapper">
                <div class="searchable-select">
                  <input
                    type="text"
                    class="form-input searchable-select-input"
                    autocomplete="off"
                    spellcheck="false"
                    placeholder="Digite para buscar cobrador..."
                    v-model="collectorFilterSearch"
                    @focus="openCollectorFilterDropdown"
                    @blur="onCollectorFilterBlur"
                    @input="onCollectorFilterInput"
                  />
                  <span class="searchable-select-arrow">▼</span>
                  <div
                    v-if="showCollectorFilterDropdown"
                    class="searchable-select-dropdown"
                  >
                    <div
                      class="searchable-select-option"
                      :class="{ 'is-selected': !selectedCollectorId }"
                      @mousedown.prevent="selectCollectorFilterAll"
                    >
                      Todos os cobradores
                    </div>
                    <div
                      v-for="c in filteredCollectors"
                      :key="c.id"
                      class="searchable-select-option"
                      :class="{ 'is-selected': String(selectedCollectorId) === String(c.id) }"
                      @mousedown.prevent="selectCollectorFilter(c)"
                    >
                      {{ c.name }}
                    </div>
                    <div
                      v-if="filteredCollectors.length === 0"
                      class="searchable-select-option no-results"
                    >
                      Nenhum cobrador encontrado
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-tie"></i>
                Vendedor
              </label>
              <div class="searchable-select-wrapper">
                <div
                  class="searchable-select"
                  :class="{ 'is-disabled': clearSellerSearch }"
                >
                  <input
                    type="text"
                    class="form-input searchable-select-input"
                    autocomplete="off"
                    spellcheck="false"
                    :disabled="clearSellerSearch"
                    :placeholder="sellerFilterPlaceholder"
                    v-model="sellerFilterSearch"
                    @focus="openSellerFilterDropdown"
                    @blur="onSellerFilterBlur"
                    @input="onSellerFilterInput"
                  />
                  <span class="searchable-select-arrow">▼</span>
                  <div
                    v-if="showSellerFilterDropdown && !clearSellerSearch"
                    class="searchable-select-dropdown"
                  >
                    <div
                      class="searchable-select-option"
                      :class="{ 'is-selected': selectedSeller === 'all' }"
                      @mousedown.prevent="selectSellerFilterAll"
                    >
                      Todos
                    </div>
                    <div
                      v-for="s in filteredAvailableSellers"
                      :key="s.id"
                      class="searchable-select-option"
                      :class="{ 'is-selected': selectedSeller === s.id }"
                      @mousedown.prevent="selectSellerFilter(s)"
                    >
                      {{ s.name }}
                    </div>
                    <div
                      v-if="filteredAvailableSellers.length === 0"
                      class="searchable-select-option no-results"
                    >
                      {{ sellerFilterEmptyMessage }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-exchange-alt"></i>
                Tipo de Transação
              </label>
              <select 
                class="form-input" 
                v-model="selectedTransactionType"
              >
                <option value="">Todos os tipos</option>
                <option :value="TransactionType.ENTRADA">{{ TransactionTypeDisplay.ENTRADA }}</option>
                <option :value="TransactionType.SAIDA">{{ TransactionTypeDisplay.SAIDA }}</option>
              </select>
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-calendar-days"></i>
                Data Início
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="financialTransactionsDateBegin"
                placeholder="Data de início"
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-calendar-days"></i>
                Data Fim
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="financialTransactionsDateEnd"
                placeholder="Data de fim"
              />
            </div>
          </div>

          <div class="filters-actions">
            <button 
              type="button" 
              class="btn-primary" 
              @click="searchFinancialTransactions(1)"
            >
              <i class="fa-solid fa-search"></i>
              Pesquisar
            </button>
          </div>
        </div>
      </div>

      <!-- Tabela de Resultados -->
      <div v-show="(sellerBalance?.financialTransactions?.length || sellerBalance?.FinancialTransactions?.length) > 0" class="results-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-table"></i>
            Lançamentos Encontrados
          </h3>
          <div class="card-header-actions">
            <div class="card-actions">
              <div class="columns-dropdown" ref="columnsDropdown">
                <button
                  type="button"
                  class="btn-action btn-columns"
                  @click.stop="showColumnsMenu = !showColumnsMenu"
                  title="Mostrar ou esconder colunas"
                >
                  <i class="fa-solid fa-columns"></i>
                  Colunas
                  <i class="fa-solid fa-caret-down columns-caret"></i>
                </button>
                <div v-if="showColumnsMenu" class="columns-menu" @click.stop>
                  <div class="columns-menu-header">
                    <span>Colunas visíveis</span>
                    <button type="button" class="columns-reset" @click="resetVisibleColumns">
                      Restaurar
                    </button>
                  </div>
                  <label
                    v-for="col in toggleableColumns"
                    :key="col.key"
                    class="columns-menu-item"
                    :class="{ locked: col.locked }"
                  >
                    <input
                      type="checkbox"
                      :checked="isColumnVisible(col.key)"
                      :disabled="col.locked"
                      @change="toggleColumn(col.key)"
                    />
                    <span>{{ col.label }}</span>
                  </label>
                </div>
              </div>
              <button 
                type="button" 
                class="btn-action btn-print" 
                @click="printReport()"
                title="Imprimir relatório"
              >
                <i class="fa-solid fa-print"></i>
                Imprimir
              </button>
              <button 
                type="button" 
                class="btn-action btn-pdf" 
                @click="exportToPDF()"
                title="Exportar para PDF"
              >
                <i class="fa-solid fa-file-pdf"></i>
                PDF
              </button>
              <button 
                type="button" 
                class="btn-action btn-excel" 
                @click="exportToExcel()"
                title="Exportar para Excel"
              >
                <i class="fa-solid fa-file-excel"></i>
                Excel
              </button>
            </div>
            <button 
              v-if="canCreateEditTransactions"
              type="button" 
              class="btn-primary" 
              @click="createFinancialTransactions()"
            >
              <i class="fa-solid fa-plus"></i>
              Adicionar Lançamento
            </button>
          </div>
        </div>
        
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th v-if="isColumnVisible('seller')">Vendedor</th>
                <th v-if="isColumnVisible('description')">Descrição</th>
                <th v-if="isColumnVisible('amountDueBefore')">Saldo anterior</th>
                <th v-if="isColumnVisible('value')">Valor</th>
                <th v-if="isColumnVisible('amountOpenAfter')">Saldo final</th>
                <th v-if="isColumnVisible('type')">Tipo</th>
                <th v-if="isColumnVisible('regionDestino')">Região Destino</th>
                <th v-if="isColumnVisible('confirmed')">Confirmado</th>
                <th v-if="isColumnVisible('launchedBy')">Lançado por</th>
                <th v-if="isColumnVisible('createdAt')">Data/Hora Criação</th>
                <th v-if="canCreateEditTransactions || hasDescargaToApprove" width="150">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="r in (sellerBalance?.financialTransactions || sellerBalance?.FinancialTransactions || [])" 
                :key="r.id"
                class="data-row"
              >
                <td v-if="isColumnVisible('seller')">
                  <div class="seller-cell">
                    <i class="fa-solid fa-user"></i>
                    <span>{{ r.seller || r.User || r.user || 'Banca' }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('description')">
                  <span class="description-text">{{ r.description }}</span>
                </td>
                <td v-if="isColumnVisible('amountDueBefore')">
                  <template v-if="hasChargeBalance(r)">
                    <span
                      class="currency-value"
                      :class="balanceValueClass(r.amountDueBefore ?? r.AmountDueBefore)"
                    >
                      {{ setCurrencyString(r.amountDueBefore ?? r.AmountDueBefore) }}
                    </span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td v-if="isColumnVisible('value')">
                  <span class="currency-value">{{ setCurrencyString(r.value) }}</span>
                </td>
                <td v-if="isColumnVisible('amountOpenAfter')">
                  <template v-if="hasChargeBalance(r)">
                    <span
                      class="currency-value"
                      :class="balanceValueClass(r.amountOpenAfter ?? r.AmountOpenAfter)"
                    >
                      {{ setCurrencyString(r.amountOpenAfter ?? r.AmountOpenAfter) }}
                    </span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td v-if="isColumnVisible('type')">
                  <span :class="['type-badge', r.type === 'ENTRADA' ? 'badge-success' : 'badge-danger']">
                    <i :class="r.type === 'ENTRADA' ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'"></i>
                    {{ r.type === 'ENTRADA' ? TransactionTypeDisplay.ENTRADA : TransactionTypeDisplay.SAIDA }}
                  </span>
                </td>
                <td v-if="isColumnVisible('regionDestino')">
                  <div v-if="r.regionIdDestino || r.RegionIdDestino" class="region-cell">
                    <i class="fa-solid fa-map-marked-alt"></i>
                    <span>{{ getRegionNameById(r.regionIdDestino || r.RegionIdDestino) || 'N/A' }}</span>
                  </div>
                  <div v-else class="region-cell">
                    <span class="text-muted">-</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('confirmed')">
                  <span :class="['confirmed-badge', r.confirmed ? 'badge-confirmed' : 'badge-pending']">
                    <i :class="r.confirmed ? 'fa-solid fa-check-circle' : 'fa-solid fa-clock'"></i>
                    {{ r.confirmed ? 'Confirmado' : 'Pendente' }}
                  </span>
                </td>
                <td v-if="isColumnVisible('launchedBy')">
                  <div class="user-cell">
                    <i class="fa-solid fa-user-circle"></i>
                    <span>{{ r.User || r.user || 'Sistema' }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('createdAt')">
                  <div class="datetime-cell">
                    <i class="fa-solid fa-clock"></i>
                    <span>{{ setDateTimeDisplay(r.createdAt || r.date) }}</span>
                  </div>
                </td>
                <td v-if="canCreateEditTransactions || hasDescargaToApprove">
                  <div class="action-buttons">
                    <!-- Botão de aprovação de descarga -->
                    <button 
                      v-if="shouldShowDescargaApprovalButton(r)"
                      class="btn-action btn-warning" 
                      @click="openDescargaApprovalModal(r)"
                      title="Aprovar/Rejeitar Descarga"
                    >
                      <i class="fa-solid fa-exclamation-triangle"></i>
                    </button>
                    
                    <!-- Botões normais de edição/exclusão -->
                    <template v-if="canCreateEditTransactions">
                      <!-- Se é cobrador e lançamento da banca (sem vendedor), não mostrar botões -->
                      <template v-if="!isCobrador || r.sellerId">
                        <button 
                          class="btn-action btn-edit" 
                          @click="editFinancialTransactions(r.id)"
                          title="Editar"
                        >
                          <i class="fa-solid fa-pen-to-square"></i>
                        </button>
                        <button 
                          class="btn-action btn-delete" 
                          @click="deleteFinancialTransactions(r.id)"
                          title="Excluir"
                        >
                          <i class="fa-solid fa-trash"></i>
                        </button>
                      </template>
                      <!-- Mensagem visual para cobradores em lançamentos da banca -->
                      <span v-else-if="isCobrador && !r.sellerId" style="font-size: 11px; color: #9ca3af;">
                        Sem permissão
                      </span>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div v-if="pages.length > 1" class="pagination-container">
          <div class="pagination">
            <button
              v-for="p in pages"
              :key="p"
              :class="['page-btn', { active: p === pageIndex }]"
              @click="searchFinancialTransactions(p)"
            >
              {{ p }}
            </button>
          </div>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-show="!(sellerBalance?.financialTransactions?.length || sellerBalance?.FinancialTransactions?.length)" class="empty-state">
        <div class="empty-state-content">
          <i class="fa-solid fa-file-invoice-dollar"></i>
          <h3>Nenhum lançamento encontrado</h3>
          <p>Configure os filtros e clique em pesquisar para visualizar os lançamentos</p>
          <button 
            v-if="canCreateEditTransactions"
            type="button" 
            class="btn-primary" 
            @click="createFinancialTransactions()"
          >
            <i class="fa-solid fa-plus"></i>
            Criar Primeiro Lançamento
          </button>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2 && canCreateEditTransactions" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-file-invoice-dollar"></i>
            {{ financialTransactions.id ? 'Editar Lançamento' : 'Novo Lançamento' }}
            <p class="form-subtitle">
              {{ financialTransactions.id ? 'Atualize as informações do lançamento' : 'Preencha as informações do novo lançamento' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-building"></i>
                Lançamento da Banca
              </label>
              <div class="checkbox-container">
                <input 
                  class="form-checkbox" 
                  type="checkbox" 
                  id="ignoreSeller" 
                  v-model="ignoreSeller" 
                  :disabled="!canCreateEditBancaTransactions"
                />
                <label class="checkbox-label" for="ignoreSeller">
                  Este lançamento é apenas para a banca (sem vendedor específico)
                  <span v-if="!canCreateEditBancaTransactions" style="color: #ef4444; font-size: 11px;">
                    (Você não tem permissão para lançamentos da banca)
                  </span>
                </label>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-tie"></i>
                Vendedor
              </label>
              <div class="searchable-select-wrapper">
                <div
                  class="searchable-select"
                  :class="{ 'is-disabled': ignoreSeller }"
                >
                  <input
                    type="text"
                    class="form-input searchable-select-input"
                    autocomplete="off"
                    spellcheck="false"
                    :disabled="ignoreSeller"
                    :placeholder="sellerFormPlaceholder"
                    v-model="sellerFormSearch"
                    @focus="openSellerFormDropdown"
                    @blur="onSellerFormBlur"
                    @input="onSellerFormInput"
                  />
                  <span class="searchable-select-arrow">▼</span>
                  <div
                    v-if="showSellerFormDropdown && !ignoreSeller"
                    class="searchable-select-dropdown"
                  >
                    <div
                      v-for="s in filteredFormSellers"
                      :key="s.id"
                      class="searchable-select-option"
                      :class="{ 'is-selected': financialTransactions.sellerId === s.id }"
                      @mousedown.prevent="selectSellerForm(s)"
                    >
                      {{ s.name }}
                    </div>
                    <div
                      v-if="filteredFormSellers.length === 0"
                      class="searchable-select-option no-results"
                    >
                      {{ sellerFilterEmptyMessage }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-file-text"></i>
                Descrição
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="financialTransactions.description"
                placeholder="Digite a descrição do lançamento..."
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-calendar-days"></i>
                Data
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="financialTransactions.date"
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-exchange-alt"></i>
                Tipo
              </label>
              <select class="form-input" v-model="financialTransactions.type">
                <option value="">Selecione o tipo</option>
                <option :value="TransactionType.ENTRADA">{{ TransactionTypeDisplay.ENTRADA }}</option>
                <option :value="TransactionType.SAIDA">{{ TransactionTypeDisplay.SAIDA }}</option>
              </select>
            </div>

            <!-- Saldo do vendedor: anterior → valor → final -->
            <template v-if="showSellerBalanceFields">
              <div class="input-group">
                <label class="input-label" :class="{ required: financialTransactions.type === TransactionType.ENTRADA }">
                  <i class="fa-solid fa-wallet"></i>
                  Saldo anterior
                </label>
                <input
                  class="form-input"
                  type="number"
                  step="0.01"
                  v-model="financialTransactions.amountDueBefore"
                  placeholder="0,00"
                  :disabled="loadingSellerBalance"
                />
                <small class="field-hint">
                  <template v-if="loadingSellerBalance">Carregando saldo do vendedor...</template>
                  <template v-else>
                    Preenchido com o Saldo Atual do período (mesmo do Fechamento). Positivo = a pagar; negativo = a receber.
                  </template>
                </small>
              </div>

              <div class="input-group">
                <label class="input-label required">
                  <i class="fa-solid fa-dollar-sign"></i>
                  Valor
                </label>
                <input
                  class="form-input"
                  type="number"
                  step="0.01"
                  v-model="financialTransactions.value"
                  placeholder="0,00"
                />
              </div>

              <div class="input-group">
                <label class="input-label">
                  <i class="fa-solid fa-scale-balanced"></i>
                  Saldo final
                </label>
                <div
                  class="balance-final-box"
                  :class="{
                    'is-pay': sellerBalanceFinal > 0,
                    'is-receive': sellerBalanceFinal < 0,
                    'is-zero': sellerBalanceFinal === 0,
                  }"
                >
                  <span class="balance-final-value">
                    {{ formatCurrency(sellerBalanceFinal) }}
                  </span>
                  <span class="balance-final-label">
                    {{ sellerBalanceFinalLabel }}
                  </span>
                </div>
                <small class="field-hint">
                  {{ financialTransactions.type === TransactionType.SAIDA
                    ? 'Saldo anterior + valor (Banca Pagou)'
                    : 'Saldo anterior − valor (Banca Recebeu)' }}
                </small>
              </div>
            </template>

            <div v-else class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-dollar-sign"></i>
                Valor
              </label>
              <input 
                class="form-input" 
                type="number" 
                step="0.01" 
                v-model="financialTransactions.value"
                placeholder="0,00"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-file-invoice"></i>
                Tipo de Lançamento
              </label>
              <div class="checkbox-container">
                <input 
                  class="form-checkbox" 
                  type="checkbox" 
                  id="descarga" 
                  v-model="financialTransactions.descarga"
                  :disabled="financialTransactions.id"
                />
                <label class="checkbox-label" for="descarga">
                  Este é um lançamento de descarga
                </label> 
              </div>

            </div>

            <!-- Campo Região Destino - apenas para descargas -->
            <div v-if="shouldShowRegionDestino" class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-map-marked-alt"></i>
                Região de Destino da Descarga
              </label>
              <select 
                class="form-input" 
                v-model="financialTransactions.regionIdDestino"
                :disabled="financialTransactions.id || (!currentSelectedRegion?.onlyDescarga && availableRegionsForDescarga.length <= 1)"
              >
                <option value="">Selecione a região de destino</option>
                <option v-for="region in availableRegionsForDescarga" :key="region.id" :value="region.id">
                  {{ region.name }}
                </option>
              </select>
            </div>

            <!-- Campo Confirmed - apenas na edição de descargas -->
            <div v-if="financialTransactions.id && (financialTransactions.descarga || financialTransactions.RegionIdDestino || financialTransactions.regionIdDestino)" class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-check-circle"></i>
                Status de Confirmação
              </label>
              <div class="checkbox-container">
                <input 
                  class="form-checkbox" 
                  type="checkbox" 
                  id="confirmed" 
                  v-model="financialTransactions.confirmed" 
                />
                <label class="checkbox-label" for="confirmed">
                  Marcar como confirmado
                </label>
              </div>

            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="financialTransactions.id ? updateFinancialTransactions() : insertFinancialTransactions()"
            :disabled="saving"
          >
            <i :class="saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-save'"></i>
            {{ saving ? 'Salvando...' : (financialTransactions.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Aprovação/Rejeição de Descarga -->
    <div v-if="showDescargaApprovalModal" class="modal-overlay" @click="closeDescargaApprovalModal">
      <div class="modal-content descarga-approval-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-exclamation-triangle"></i>
            Aprovação de Descarga
          </h3>
          <button class="modal-close-btn" @click="closeDescargaApprovalModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="descarga-info">
            <div class="info-grid">
              <div class="info-item">
                <label class="info-label">Descrição:</label>
                <span class="info-value">{{ selectedDescarga?.description }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">Valor:</label>
                <span class="info-value currency">{{ setCurrencyString(selectedDescarga?.value || 0) }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">Região Origem:</label>
                <span class="info-value">{{ selectedDescarga?.regionDescargaOrigemName || '—' }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">Vendedor Origem:</label>
                <span class="info-value">{{ selectedDescarga?.sellerOrigemName || selectedDescarga?.User || selectedDescarga?.user || 'Banca' }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">Vendedor (destino):</label>
                <span class="info-value">{{ selectedDescarga?.seller || selectedDescarga?.User || selectedDescarga?.user || 'Banca' }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">Região Destino:</label>
                <span class="info-value">{{ getRegionNameById(selectedDescarga?.regionIdDestino || selectedDescarga?.RegionIdDestino) }}</span>
              </div>
              <div class="info-item">
                <label class="info-label">Data/Hora:</label>
                <span class="info-value">{{ setDateTimeDisplay(selectedDescarga?.createdAt || selectedDescarga?.date) }}</span>
              </div>
            </div>
          </div>
          
          <div class="approval-question">
            <p class="question-text">
              <i class="fa-solid fa-question-circle"></i>
              Deseja aprovar ou rejeitar esta descarga?
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-approve"
            @click="approveDescarga()"
            :disabled="descargaActionLoading"
          >
            <i class="fa-solid fa-check" v-if="!descargaActionLoading"></i>
            <i class="fa-solid fa-spinner fa-spin" v-else></i>
            Aprovar
          </button>
          <button
            class="btn-reject"
            @click="rejectDescarga()"
            :disabled="descargaActionLoading"
          >
            <i class="fa-solid fa-times" v-if="!descargaActionLoading"></i>
            <i class="fa-solid fa-spinner fa-spin" v-else></i>
            Rejeitar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Badges de confirmação */
.confirmed-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-confirmed {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.badge-pending {
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
  border: 1px solid rgba(249, 115, 22, 0.3);
}

.badge-confirmed i {
  font-size: 11px;
}

.badge-pending i {
  font-size: 11px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Estilos existentes dos type-badges */
.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.badge-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

/* Estilos para o fundo do balance-value */
.balance-value.bg-positive {
  background: rgba(34, 197, 94, 0.8) !important;
  border: 1px solid rgba(34, 197, 94, 0.9)!important;
}

.balance-value.bg-negative {
  background: rgba(239, 68, 68, 0.8) !important;
  border: 1px solid rgba(239, 68, 68, 0.9)!important;
}

/* Estilos para célula de região */
.region-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.region-cell i {
  color: #6b7280;
  font-size: 14px;
}

.text-muted {
  color: #9ca3af;
  font-style: italic;
}

/* Badge de descarga */
.descarga-badge {
  display: block;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* Estilos para impedir quebra de linha apenas nos textos */
.table-container th,
.table-container td {
  white-space: nowrap;
}

.table-container .region-cell,
.table-container .type-badge,
.table-container .confirmed-badge {
  white-space: nowrap;
}

/* Estilos para mensagem de ajuda */
.field-help {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
}

.field-help i {
  font-size: 12px;
  color: #3b82f6;
}

.field-help span {
  line-height: 1.4;
}

/* Estilos para o modal de aprovação de descarga */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--card-background);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 20px 40px var(--shadow-hover);
  max-width: 600px;
  width: 90%;
  max-height: 90%;
  overflow-y: auto;
  position: relative;
  color: var(--text-primary);
}

.descarga-approval-modal {
  max-width: 700px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--warning-color) 0%, var(--warning-hover) 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title i {
  font-size: 24px;
}

.modal-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 32px;
  background: var(--card-background);
}

.descarga-info {
  background: var(--secondary-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
}

.info-value.currency {
  color: var(--success);
  font-weight: 600;
}

.approval-question {
  text-align: center;
  padding: 24px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.05) 100%);
  border: 1px solid var(--warning-color);
  border-radius: 8px;
}

[data-theme="dark"] .approval-question {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(251, 191, 36, 0.03) 100%);
  border: 1px solid var(--warning-color);
}

.question-text {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.question-text i {
  font-size: 20px;
  color: var(--warning-color);
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 24px 32px;
  border-top: 1px solid var(--border-color);
  background: var(--secondary-color);
  border-radius: 0 0 12px 12px;
  justify-content: flex-end;
}

.btn-approve {
  background: linear-gradient(135deg, var(--success) 0%, #059669 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-approve:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.3);
}

.btn-approve:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-reject {
  background: linear-gradient(135deg, var(--danger-color) 0%, var(--danger-hover) 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-reject:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.3);
}

.btn-reject:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Estilos para botão de alerta de descarga */
.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  border: 1px solid #f59e0b;
  animation: pulse-warning 2s infinite;
}

.btn-warning:hover {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  border-color: #f97316;
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.4);
}

@keyframes pulse-warning {
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.8; 
    transform: scale(1.05);
  }
}

/* Responsividade para o modal */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .modal-header {
    padding: 20px;
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-footer {
    padding: 20px;
  }
}

/* Card header com ações — sobrescreve .btn-action global (32x32 ícone) */
.results-card {
  overflow: visible !important;
}

/* Dropdowns (cobrador/vendedor) não podem ser cortados pelo card */
.financial-transactions-container :deep(.filters-card),
.filters-card,
.financial-transactions-container :deep(.form-card),
.form-card {
  overflow: visible !important;
  position: relative;
}

.filters-card {
  z-index: 40;
}

.form-card {
  z-index: 30;
}

.filters-content,
.filters-grid,
.form-body,
.form-grid {
  overflow: visible !important;
  position: relative;
}

.filters-grid .input-group,
.form-grid .input-group {
  overflow: visible !important;
  position: relative;
  z-index: 1;
}

.filters-grid .input-group:focus-within,
.form-grid .input-group:focus-within {
  z-index: 80;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 6;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.card-actions .btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.card-actions .btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.card-actions .btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.card-actions .btn-action:active {
  transform: translateY(0);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .card-header-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
  
  .card-actions {
    width: 100%;
  }
  
  .card-actions .btn-action {
    flex: 1 1 auto;
    justify-content: center;
    width: auto !important;
    height: auto !important;
  }

  .columns-dropdown {
    flex: 1 1 auto;
  }

  .columns-dropdown .btn-action {
    width: 100% !important;
  }

  .columns-menu {
    left: 0;
    right: 0;
    min-width: 0;
  }
}

/* Select digitável de vendedor (filtro e formulário) */
.searchable-select-wrapper {
  position: relative;
  width: 100%;
  z-index: 2;
  overflow: visible !important;
}

.searchable-select {
  position: relative;
  overflow: visible !important;
}

.searchable-select.is-disabled {
  opacity: 0.65;
}

.searchable-select-input {
  width: 100%;
  padding-right: 2rem !important;
}

.searchable-select-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary, #6b7280);
  font-size: 0.7rem;
}

.searchable-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 9999;
  max-height: 240px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
}

[data-theme="dark"] .searchable-select-dropdown {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.searchable-select-option {
  padding: 0.65rem 0.85rem;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.searchable-select-option:hover {
  background: rgba(37, 99, 235, 0.08);
}

.searchable-select-option.is-selected {
  background: rgba(37, 99, 235, 0.14);
  font-weight: 600;
}

.searchable-select-option.no-results {
  cursor: default;
  color: var(--text-secondary, #6b7280);
  font-style: italic;
}

.searchable-select-option.no-results:hover {
  background: transparent;
}

.field-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-secondary, #6b7280);
  line-height: 1.35;
}

.balance-final-box {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  background: var(--bg-secondary, #f8fafc);
  min-height: 52px;
  justify-content: center;
}

.balance-final-value {
  font-size: 1.15rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}

.balance-final-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
}

.balance-final-box.is-pay .balance-final-value {
  color: #c62828;
}

.balance-final-box.is-receive .balance-final-value {
  color: #2e7d32;
}

.balance-final-box.is-zero .balance-final-value {
  color: var(--text-primary);
}
</style>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const FINANCIAL_TRANSACTIONS_COLUMNS_STORAGE_KEY = "financialTransactions.visibleColumns";

const DEFAULT_VISIBLE_COLUMNS = {
  seller: true,
  description: true,
  amountDueBefore: true,
  value: true,
  amountOpenAfter: true,
  type: true,
  regionDestino: true,
  confirmed: true,
  launchedBy: true,
  createdAt: true,
};

export default {
  data: () => ({
    ignoreSeller: false,
    clearSellerSearch: true, 
    sellerBalance: {},
    financialTransactions: {},
    selectedTab: 1,
    loading: true,
    saving: false,
    sellers: [],
    collectors: [],
    selectedCollectorId: null,
    collectorFilterSearch: "",
    showCollectorFilterDropdown: false,
    selectedSeller: null,
    selectedSellerName: "",
    sellerFilterSearch: "",
    showSellerFilterDropdown: false,
    sellerFormSearch: "",
    showSellerFormDropdown: false,
    loadingSellerBalance: false,
    /** Região/banca do header — reativos via eventos regionChanged/bancaChanged */
    headerRegion: (() => {
      try {
        const raw = localStorage.getItem("currentRegion");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })(),
    headerBanca: (() => {
      try {
        const raw = localStorage.getItem("selectedBanca");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })(),
    selectedTransactionType: "", // Novo filtro para tipo de transação
    financialTransactionsDateBegin: "",
    financialTransactionsDateEnd: "",
    pages: [],
    pageIndex: 0,
    
    // Controle de usuário e permissões
    currentUser: null,
    userResponsibleRegions: [],
    bancas: [],
    regions: [],
    regionsForDescarga: [], // Regiões disponíveis para descarga
    
    // Tipos de transação
    TransactionType: {
      ENTRADA: 'ENTRADA',
      SAIDA: 'SAIDA'
    },
    TransactionTypeDisplay: {
      ENTRADA: 'Banca Recebeu',
      SAIDA: 'Banca Pagou'
    },

    // Modal de Aprovação/Rejeição de Descarga
    showDescargaApprovalModal: false,
    selectedDescarga: null,
    descargaActionLoading: false,

    // Controle de colunas da tabela
    showColumnsMenu: false,
    visibleColumns: { ...DEFAULT_VISIBLE_COLUMNS },
  }),
  computed: {
    currentSelectedRegion() {
      return this.headerRegion;
    },

    currentSelectedBanca() {
      return this.headerBanca;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    canCreateEditTransactions() {
      // ADMIN: acesso total
      // BANCA e GERENTE: podem criar/editar/excluir
      // COBRADOR: pode criar/editar/excluir apenas lançamentos de vendedor
      return this.isAdmin || this.isBanca || this.isGerente || this.isCobrador;
    },

    // Nova computed para verificar se pode criar/editar lançamentos da banca
    canCreateEditBancaTransactions() {
      // Apenas ADMIN, BANCA e GERENTE podem criar/editar lançamentos da banca
      // COBRADOR não pode
      return this.isAdmin || this.isBanca || this.isGerente;
    },

    availableBancasForUser() {
      if (!this.bancas || !Array.isArray(this.bancas)) {
        return [];
      }
      
      if (this.isAdmin) {
        // ADMIN vê todas as bancas
        return this.bancas;
      } else if (this.isBanca && this.currentUser) {
        // BANCA vê apenas ela mesma
        return this.bancas.filter(banca => 
          banca && banca.responsibleUserIds && Array.isArray(banca.responsibleUserIds) &&
          banca.responsibleUserIds.includes(this.currentUser.id)
        );
      } else {
        // GERENTE e COBRADOR: mostrar bancas das regiões onde eles atuam
        return this.bancas.filter(banca => {
          if (!banca || !banca.regionIds || !Array.isArray(banca.regionIds)) return false;
          if (!this.userResponsibleRegions || !Array.isArray(this.userResponsibleRegions)) return false;
          
          return banca.regionIds.some(regionId => 
            regionId && this.userResponsibleRegions.includes(regionId)
          );
        });
      }
    },

    availableSellers() {
      if (!this.sellers || !Array.isArray(this.sellers)) {
        return [];
      }

      let sellers = this.sellers.filter((seller) => seller && seller.regionId);

      // Banca do usuário (header ou bancas permitidas pelo role)
      const bancaIds = this.currentSelectedBanca?.id
        ? [String(this.currentSelectedBanca.id)]
        : (this.availableBancasForUser || [])
            .map((b) => (b?.id != null ? String(b.id) : null))
            .filter(Boolean);

      if (this.currentSelectedRegion?.id) {
        // Com região no header: só vendedores dessa região
        sellers = sellers.filter(
          (seller) =>
            String(seller.regionId) === String(this.currentSelectedRegion.id),
        );
      } else if (bancaIds.length > 0) {
        // Sem região: todos os vendedores da(s) banca(s) do usuário
        sellers = sellers.filter(
          (seller) =>
            seller.bancaId != null && bancaIds.includes(String(seller.bancaId)),
        );
      } else if (!this.isAdmin) {
        // Sem banca identificável: restringe às regiões do usuário
        if (this.userResponsibleRegions?.length) {
          sellers = sellers.filter((seller) =>
            this.userResponsibleRegions.some(
              (id) => String(id) === String(seller.regionId),
            ),
          );
        } else {
          return [];
        }
      }

      if (!this.isAdmin && this.userResponsibleRegions?.length) {
        sellers = sellers.filter((seller) =>
          this.userResponsibleRegions.some(
            (id) => String(id) === String(seller.regionId),
          ),
        );
      }

      if (this.selectedCollectorId) {
        sellers = sellers.filter(
          (seller) =>
            String(seller.collectorId || seller.CollectorId || "") ===
            String(this.selectedCollectorId),
        );
      }

      return [...sellers].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "pt-BR", {
          sensitivity: "base",
        }),
      );
    },

    filteredCollectors() {
      let list = this.collectors || [];
      if (this.currentSelectedRegion?.id) {
        list = list.filter(
          (c) =>
            String(c.regionId || c.RegionId || "") ===
            String(this.currentSelectedRegion.id),
        );
      }
      const q = (this.collectorFilterSearch || "").trim().toLowerCase();
      if (!q || q === "todos os cobradores") {
        return list;
      }
      if (this.selectedCollectorId) {
        const selected = list.find(
          (c) => String(c.id) === String(this.selectedCollectorId),
        );
        if (selected && (selected.name || "").toLowerCase() === q) {
          return list;
        }
      }
      return list.filter((c) => (c.name || "").toLowerCase().includes(q));
    },

    filteredAvailableSellers() {
      const q = (this.sellerFilterSearch || "").trim().toLowerCase();
      if (!q || q === "todos") {
        return this.availableSellers;
      }
      return this.availableSellers.filter((s) =>
        (s.name || "").toLowerCase().includes(q),
      );
    },

    filteredFormSellers() {
      const q = (this.sellerFormSearch || "").trim().toLowerCase();
      if (!q) {
        return this.availableSellers;
      }
      return this.availableSellers.filter((s) =>
        (s.name || "").toLowerCase().includes(q),
      );
    },

    sellerFilterPlaceholder() {
      if (this.clearSellerSearch) {
        return "Lançamentos da banca";
      }
      if (this.selectedSeller === "all") {
        return "Todos";
      }
      if (!this.currentSelectedRegion?.id) {
        return "Digite para filtrar (todos da banca)...";
      }
      return "Digite para filtrar o vendedor...";
    },

    sellerFormPlaceholder() {
      if (this.ignoreSeller) {
        return "Lançamento da banca";
      }
      if (!this.currentSelectedRegion?.id) {
        return "Digite para filtrar (todos da banca)...";
      }
      return "Digite para filtrar o vendedor...";
    },

    sellerFilterEmptyMessage() {
      if (!this.currentSelectedRegion?.id) {
        return "Nenhum vendedor encontrado na banca";
      }
      return "Nenhum vendedor encontrado";
    },

    // Controlar se deve mostrar o campo região destino
    shouldShowRegionDestino() {
      // Mostrar quando é uma nova descarga OU quando está editando uma descarga existente
      return this.isDescargaTransaction && this.currentRegionReceivesDescarga;
    },

    // Verificar se é uma transação de descarga baseado no checkbox local ou se já tem RegionIdDestino
    isDescargaTransaction() {
      return this.financialTransactions.descarga || 
             !!(this.financialTransactions.RegionIdDestino || this.financialTransactions.regionIdDestino);
    },

    // Verificar se a região atual recebe descarga
    currentRegionReceivesDescarga() {
      // Aqui seria implementada a lógica para verificar se a região atual recebe descarga
      // Por enquanto, vamos assumir que todas as regiões podem receber descarga
      return true;
    },

    // Regiões disponíveis para descarga (que enviam para a região atual)
    availableRegionsForDescarga() {
      if (!this.regionsForDescarga || !Array.isArray(this.regionsForDescarga)) {
        return [];
      }
      return this.regionsForDescarga;
    },

    /** Campos de saldo só em lançamento novo de vendedor (não banca). */
    showSellerBalanceFields() {
      return (
        !this.ignoreSeller &&
        !!this.financialTransactions?.sellerId &&
        !this.financialTransactions?.id
      );
    },

    sellerBalanceFinal() {
      const before = Number(this.financialTransactions?.amountDueBefore);
      const value = Number(this.financialTransactions?.value);
      if (!Number.isFinite(before) || !Number.isFinite(value)) {
        return null;
      }
      if (this.financialTransactions.type === this.TransactionType.SAIDA) {
        return before + value;
      }
      // ENTRADA (Banca Recebeu) ou tipo ainda não escolhido: anterior − valor
      return before - value;
    },

    sellerBalanceFinalLabel() {
      const v = this.sellerBalanceFinal;
      if (v == null) {
        return "Informe saldo anterior e valor";
      }
      if (v > 0) {
        return "A pagar";
      }
      if (v < 0) {
        return "A receber";
      }
      return "Zerado";
    },

    // Verificar se há descargas pendentes para aprovação
    hasDescargaToApprove() {
      if (!this.sellerBalance) {
        return false;
      }
      
      // Verificar em ambos os arrays possíveis
      const transactions = this.sellerBalance.financialTransactions || this.sellerBalance.FinancialTransactions || [];
      
      if (!Array.isArray(transactions) || transactions.length === 0) {
        return false;
      }
      
      return transactions.some(transaction => {
        // Uma transação é considerada de descarga se tem RegionIdDestino definido
        const regionDestino = transaction.RegionIdDestino || transaction.regionIdDestino;
        if (!regionDestino) {
          return false;
        }
        
        // Verificar se está pendente (não confirmada)
        if (transaction.confirmed) {
          return false;
        }
        
        // ADMIN pode aprovar qualquer descarga
        if (this.isAdmin) {
          return true;
        }
        
        // Verificar se o usuário é responsável pela região destino da descarga
        if (this.userResponsibleRegions && Array.isArray(this.userResponsibleRegions)) {
          return this.userResponsibleRegions.includes(regionDestino);
        }
        
        return false;
      });
    },

    toggleableColumns() {
      return [
        { key: "seller", label: "Vendedor", locked: true },
        { key: "description", label: "Descrição" },
        { key: "amountDueBefore", label: "Saldo anterior" },
        { key: "value", label: "Valor" },
        { key: "amountOpenAfter", label: "Saldo final" },
        { key: "type", label: "Tipo" },
        { key: "regionDestino", label: "Região Destino" },
        { key: "confirmed", label: "Confirmado" },
        { key: "launchedBy", label: "Lançado por" },
        { key: "createdAt", label: "Data/Hora Criação" },
      ];
    },
  },
  async mounted() {
    this.loadVisibleColumns();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    window.addEventListener("regionChanged", this.onHeaderRegionChanged);
    window.addEventListener("bancaChanged", this.onHeaderBancaChanged);
    this.loading = true;
    try {
      await this.loadCurrentUser();
      await this.loadBancas();
      await this.loadRegions();
      await this.loadRegionsForDescarga();
      
      // Para usuários BANCA, complementar regiões responsáveis com regiões das bancas
      if (this.isBanca) {
        await this.loadBancaRegions();
      }
      
      await this.searchSellers();
      await this.loadCollectors();
      
      const _initNow = new Date();
      let _initFrom = new Date();
      _initFrom.setMonth(_initFrom.getMonth() - 1);
      const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      this.financialTransactionsDateBegin = _fmt(_initFrom);
      this.financialTransactionsDateEnd = _fmt(_initNow);
      this.financialTransactions.date = _fmt(_initNow);
      
      // Inicializar com o usuário logado
      this.financialTransactions.userId = this.currentUser?.id;
      
      // Adicionar o RegionId obrigatório baseado na região selecionada
      this.financialTransactions.RegionId = this.currentSelectedRegion?.id;
      
      // Adicionar campo Confirmed (será definido pela regra de negócio)
      this.financialTransactions.confirmed = false;
      
      // Inicializar campos de descarga
      this.financialTransactions.descarga = false;
      this.financialTransactions.regionIdDestino = null;
      
      // Buscar resultados automaticamente ao carregar a página
      this.searchFinancialTransactions(1);
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
    window.removeEventListener("regionChanged", this.onHeaderRegionChanged);
    window.removeEventListener("bancaChanged", this.onHeaderBancaChanged);
  },
  watch: {
    ignoreSeller(newVal) {
      if (newVal) {
        this.financialTransactions.sellerId = null;
        this.financialTransactions.amountDueBefore = null;
        this.sellerFormSearch = "";
      }
    },

    'financialTransactions.sellerId'(newVal) {
      if (!newVal || this.ignoreSeller || this.financialTransactions?.id) {
        return;
      }
      this.loadSellerCurrentBalance(newVal);
    },

    'financialTransactions.type'(newVal) {
      if (
        newVal === this.TransactionType.ENTRADA &&
        this.financialTransactions?.sellerId &&
        !this.financialTransactions?.id &&
        (this.financialTransactions.amountDueBefore === null ||
          this.financialTransactions.amountDueBefore === undefined ||
          this.financialTransactions.amountDueBefore === "")
      ) {
        this.loadSellerCurrentBalance(this.financialTransactions.sellerId);
      }
    },

    // Controlar regionIdDestino quando descarga for marcado/desmarcado
    'financialTransactions.descarga'(newVal) {
      // Se é uma edição, não permitir alteração
      if (this.financialTransactions.id) {
        return;
      }
      
      if (!newVal) {
        // Se desmarcou descarga, limpar region destino
        this.financialTransactions.regionIdDestino = null;
      } else {
        // Se marcou descarga, auto-preencher baseado no tipo de região
        if (this.currentSelectedRegion && !this.currentSelectedRegion.onlyDescarga) {
          // Se a região atual NÃO é de descarga, auto-preencher com o DescargaRegionId
          this.financialTransactions.regionIdDestino = this.currentSelectedRegion.descargaRegionId;
        } else if (this.currentSelectedRegion && this.currentSelectedRegion.onlyDescarga) {
          // Se a região atual é de descarga, auto-preencher com o DescargaRegionId (redescarga) se houver
          this.financialTransactions.regionIdDestino = this.currentSelectedRegion.descargaRegionId || null;
        } else {
          // Se não há região selecionada, deixar vazio
          this.financialTransactions.regionIdDestino = null;
        }
      }
    },
    clearSellerSearch(newVal){
      if(newVal){
        this.selectedSeller = null;
        this.selectedSellerName = "Banca";
        this.sellerFilterSearch = "";
        this.showSellerFilterDropdown = false;
        // Buscar automaticamente quando mudar para lançamentos da banca
        if (this.financialTransactionsDateBegin && this.financialTransactionsDateEnd) {
          this.searchFinancialTransactions(1);
        }
      } else {
        this.selectedSellerName = "";
        this.sellerFilterSearch = "";
      }
    },

    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Se a região mudou, limpar seleções e resetar para lançamentos da banca
        this.selectedSeller = null;
        this.clearSellerSearch = true;
        this.selectedSellerName = "Banca";
        this.sellerFilterSearch = "";
        this.sellerFormSearch = "";
        this.financialTransactions.sellerId = null;
        this.selectedTransactionType = "";
        this.sellerBalance = {};
        
        // Recarregar regiões disponíveis para descarga
        this.loadRegionsForDescarga();
        
        // Buscar automaticamente se as datas estão definidas
        if (this.financialTransactionsDateBegin && this.financialTransactionsDateEnd) {
          this.searchFinancialTransactions(1);
        }
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Se a banca mudou, limpar seleções e resetar para lançamentos da banca
        this.selectedSeller = null;
        this.clearSellerSearch = true;
        this.selectedSellerName = "Banca";
        this.sellerFilterSearch = "";
        this.sellerFormSearch = "";
        this.financialTransactions.sellerId = null;
        this.selectedTransactionType = "";
        this.sellerBalance = {};
        
        // Buscar automaticamente se as datas estão definidas
        if (this.financialTransactionsDateBegin && this.financialTransactionsDateEnd) {
          this.searchFinancialTransactions(1);
        }
      },
      deep: true
    }
  },
  methods: {
    onHeaderRegionChanged(event) {
      this.headerRegion = event?.detail?.region ?? null;
      this.syncCollectorFilterToRegion();
      this.loadCollectors();
    },

    onHeaderBancaChanged(event) {
      this.headerBanca = event?.detail?.banca ?? null;
    },

    openCollectorFilterDropdown() {
      this.showCollectorFilterDropdown = true;
    },

    onCollectorFilterBlur() {
      window.setTimeout(() => {
        this.showCollectorFilterDropdown = false;
        this.syncCollectorFilterSearchLabel();
      }, 150);
    },

    onCollectorFilterInput() {
      this.showCollectorFilterDropdown = true;
      if (this.selectedCollectorId) {
        const selected = (this.collectors || []).find(
          (c) => String(c.id) === String(this.selectedCollectorId),
        );
        if (
          !selected ||
          (selected.name || "").toLowerCase() !==
            (this.collectorFilterSearch || "").trim().toLowerCase()
        ) {
          this.selectedCollectorId = null;
          this.clearSellerIfNotInAvailable();
        }
      }
    },

    selectCollectorFilterAll() {
      this.selectedCollectorId = null;
      this.collectorFilterSearch = "";
      this.showCollectorFilterDropdown = false;
    },

    selectCollectorFilter(collector) {
      this.selectedCollectorId = collector.id;
      this.collectorFilterSearch = collector.name || "";
      this.showCollectorFilterDropdown = false;
      this.clearSellerIfNotInAvailable();
    },

    syncCollectorFilterSearchLabel() {
      if (!this.selectedCollectorId) {
        this.collectorFilterSearch = "";
        return;
      }
      const selected = (this.collectors || []).find(
        (c) => String(c.id) === String(this.selectedCollectorId),
      );
      this.collectorFilterSearch = selected?.name || "";
    },

    syncCollectorFilterToRegion() {
      if (!this.selectedCollectorId || !this.currentSelectedRegion?.id) return;
      const stillValid = (this.collectors || []).some(
        (c) =>
          String(c.id) === String(this.selectedCollectorId) &&
          String(c.regionId || c.RegionId || "") ===
            String(this.currentSelectedRegion.id),
      );
      if (!stillValid) {
        this.selectedCollectorId = null;
        this.collectorFilterSearch = "";
        this.clearSellerIfNotInAvailable();
      }
    },

    clearSellerIfNotInAvailable() {
      if (!this.selectedSeller || this.selectedSeller === "all") return;
      const stillThere = this.availableSellers.some(
        (s) => String(s.id) === String(this.selectedSeller),
      );
      if (!stillThere) {
        this.selectedSeller = "all";
        this.sellerFilterSearch = "Todos";
        this.selectedSellerName = "Todos";
      }
    },

    async loadCollectors() {
      try {
        const filters = {};
        if (this.currentSelectedRegion?.id) {
          filters.regionId = this.currentSelectedRegion.id;
        }
        const res = await mainService.searchCollectors(filters);
        const list = res?.data || [];
        this.collectors = Array.isArray(list)
          ? [...list].sort((a, b) =>
              (a.name || "").localeCompare(b.name || "", "pt-BR", {
                sensitivity: "base",
              }),
            )
          : [];
        this.syncCollectorFilterToRegion();
        this.syncCollectorFilterSearchLabel();
      } catch (e) {
        this.collectors = [];
      }
    },

    openSellerFilterDropdown() {
      if (this.clearSellerSearch) {
        return;
      }
      this.showSellerFilterDropdown = true;
    },

    onSellerFilterBlur() {
      window.setTimeout(() => {
        this.showSellerFilterDropdown = false;
        this.syncSellerFilterSearchLabel();
      }, 150);
    },

    onSellerFilterInput() {
      this.showSellerFilterDropdown = true;
      // Digitando invalida seleção específica (exceto se voltar a bater o nome)
      if (this.selectedSeller && this.selectedSeller !== "all") {
        const selected = this.availableSellers.find(
          (s) => String(s.id) === String(this.selectedSeller),
        );
        if (
          !selected ||
          (selected.name || "").toLowerCase() !==
            (this.sellerFilterSearch || "").trim().toLowerCase()
        ) {
          this.selectedSeller = null;
        }
      } else if (this.selectedSeller === "all") {
        if ((this.sellerFilterSearch || "").trim().toLowerCase() !== "todos") {
          this.selectedSeller = null;
        }
      }
    },

    selectSellerFilterAll() {
      this.selectedSeller = "all";
      this.sellerFilterSearch = "Todos";
      this.selectedSellerName = "Todos";
      this.showSellerFilterDropdown = false;
    },

    selectSellerFilter(seller) {
      this.selectedSeller = seller.id;
      this.sellerFilterSearch = seller.name || "";
      this.selectedSellerName = seller.name || "";
      this.showSellerFilterDropdown = false;
    },

    syncSellerFilterSearchLabel() {
      if (this.clearSellerSearch) {
        this.sellerFilterSearch = "";
        return;
      }
      if (this.selectedSeller === "all") {
        this.sellerFilterSearch = "Todos";
        return;
      }
      if (this.selectedSeller) {
        const selected = this.availableSellers.find(
          (s) => String(s.id) === String(this.selectedSeller),
        );
        this.sellerFilterSearch = selected?.name || "";
        return;
      }
    },

    openSellerFormDropdown() {
      if (this.ignoreSeller) {
        return;
      }
      this.showSellerFormDropdown = true;
    },

    onSellerFormBlur() {
      window.setTimeout(() => {
        this.showSellerFormDropdown = false;
        this.syncSellerFormSearchLabel();
      }, 150);
    },

    onSellerFormInput() {
      this.showSellerFormDropdown = true;
      if (this.financialTransactions.sellerId) {
        const selected = this.availableSellers.find(
          (s) =>
            String(s.id) === String(this.financialTransactions.sellerId),
        );
        if (
          !selected ||
          (selected.name || "").toLowerCase() !==
            (this.sellerFormSearch || "").trim().toLowerCase()
        ) {
          this.financialTransactions.sellerId = null;
        }
      }
    },

    selectSellerForm(seller) {
      this.financialTransactions.sellerId = seller.id;
      this.sellerFormSearch = seller.name || "";
      this.showSellerFormDropdown = false;
      this.loadSellerCurrentBalance(seller.id);
    },

    formatCurrency(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
      }
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    /** Mesmo Saldo Atual do Fechamento por Vendedor (EntriesSeller). */
    async loadSellerCurrentBalance(sellerId) {
      if (!sellerId || this.ignoreSeller || this.financialTransactions?.id) {
        return;
      }
      this.loadingSellerBalance = true;
      try {
        const end =
          this.financialTransactionsDateEnd ||
          this.financialTransactions.date ||
          new Date().toISOString().slice(0, 10);
        let begin = this.financialTransactionsDateBegin;
        if (!begin) {
          const d = new Date(end + "T12:00:00");
          d.setMonth(d.getMonth() - 1);
          begin = d.toISOString().slice(0, 10);
        }
        const response = await mainService.searchSellerBalance({
          sellerIds: [sellerId],
          balanceDateBegin: `${begin}T00:00:00`,
          balanceDateEnd: `${end}T23:59:59`,
        });
        const data = response?.data || {};
        const item = Array.isArray(data.sellers) ? data.sellers[0] : null;
        const balance =
          item != null
            ? Number(item.currentBalance)
            : Number(data.currentBalanceAmount) || 0;
        this.financialTransactions.amountDueBefore = Number.isFinite(balance)
          ? balance
          : 0;
      } catch (error) {
        console.error("Erro ao carregar saldo do vendedor:", error);
      } finally {
        this.loadingSellerBalance = false;
      }
    },

    syncSellerFormSearchLabel() {
      if (this.ignoreSeller) {
        this.sellerFormSearch = "";
        return;
      }
      if (this.financialTransactions.sellerId) {
        const selected = this.availableSellers.find(
          (s) =>
            String(s.id) === String(this.financialTransactions.sellerId),
        );
        this.sellerFormSearch = selected?.name || "";
      }
    },

    loadVisibleColumns() {
      try {
        const saved = localStorage.getItem(FINANCIAL_TRANSACTIONS_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          seller: true,
        };
      } catch {
        this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      }
    },

    saveVisibleColumns() {
      localStorage.setItem(
        FINANCIAL_TRANSACTIONS_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visibleColumns),
      );
    },

    isColumnVisible(key) {
      if (key === "seller") {
        return true;
      }
      return this.visibleColumns[key] !== false;
    },

    toggleColumn(key) {
      if (key === "seller") {
        return;
      }
      this.visibleColumns = {
        ...this.visibleColumns,
        [key]: !this.isColumnVisible(key),
      };
      this.saveVisibleColumns();
    },

    resetVisibleColumns() {
      this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      this.saveVisibleColumns();
    },

    closeColumnsMenu() {
      this.showColumnsMenu = false;
    },

    handleColumnsMenuOutsideClick(event) {
      if (!this.showColumnsMenu) {
        return;
      }
      const dropdown = this.$refs.columnsDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.closeColumnsMenu();
      }
    },

    getVisibleExportColumns() {
      return this.toggleableColumns.filter(col => this.isColumnVisible(col.key));
    },

    buildExportRow(transaction, formatted = false) {
      const dueBefore = transaction.amountDueBefore ?? transaction.AmountDueBefore;
      const openAfter = transaction.amountOpenAfter ?? transaction.AmountOpenAfter;
      const values = {
        seller: transaction.seller || transaction.User || transaction.user || "Banca",
        description: transaction.description || "",
        amountDueBefore:
          dueBefore == null || dueBefore === ""
            ? "—"
            : formatted
              ? this.setCurrencyString(dueBefore)
              : dueBefore,
        value: formatted
          ? this.setCurrencyString(transaction.value || 0)
          : (transaction.value || 0),
        amountOpenAfter:
          openAfter == null || openAfter === ""
            ? "—"
            : formatted
              ? this.setCurrencyString(openAfter)
              : openAfter,
        type:
          transaction.type === "ENTRADA"
            ? this.TransactionTypeDisplay.ENTRADA
            : this.TransactionTypeDisplay.SAIDA,
        regionDestino:
          this.getRegionNameById(
            transaction.regionIdDestino || transaction.RegionIdDestino,
          ) || "-",
        confirmed: formatted
          ? (transaction.confirmed ? "Confirmado" : "Pendente")
          : (transaction.confirmed ? "Sim" : "Não"),
        launchedBy: transaction.User || transaction.user || "Sistema",
        createdAt: this.setDateTimeDisplay(transaction.createdAt || transaction.date),
      };
      return this.getVisibleExportColumns().map(col => values[col.key]);
    },

    hasChargeBalance(row) {
      const due = row?.amountDueBefore ?? row?.AmountDueBefore;
      const open = row?.amountOpenAfter ?? row?.AmountOpenAfter;
      return due != null || open != null;
    },

    balanceValueClass(value) {
      const v = Number(value);
      if (!Number.isFinite(v) || v === 0) return "";
      if (v > 0) return "currency-negative";
      return "currency-positive";
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (!this.isAdmin) {
            await this.loadUserResponsibleRegions();
          }
        } else {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push('/login');
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário:', error);
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        if (!this.bancas || this.bancas.length === 0) return;
        
        // Buscar bancas disponíveis para o usuário BANCA
        const bancasForUser = this.availableBancasForUser;
        
        if (bancasForUser && Array.isArray(bancasForUser)) {
          for (const banca of bancasForUser) {
            if (banca && banca.regionIds && Array.isArray(banca.regionIds)) {
              // Adicionar todas as regiões desta banca às regiões responsáveis
              banca.regionIds.forEach(regionId => {
                if (regionId && !this.userResponsibleRegions.includes(regionId)) {
                  this.userResponsibleRegions.push(regionId);
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar regiões das bancas:', error);
      }
    },

    createFinancialTransactions() {
      // Permitir que cobradores acessem o formulário
      // A restrição para lançamentos da banca será feita no checkbox e na validação
      if (!this.canCreateEditTransactions) {
        toast.error("Você não tem permissão para criar lançamentos financeiros!");
        return;
      }

      const _createNow = new Date();
      const _createDateStr = `${_createNow.getFullYear()}-${String(_createNow.getMonth() + 1).padStart(2, '0')}-${String(_createNow.getDate()).padStart(2, '0')}`;
      this.financialTransactions = {};
      this.financialTransactions.date = _createDateStr;
      // Capturar o usuário logado
      this.financialTransactions.userId = this.currentUser?.id;
      // Adicionar o RegionId obrigatório baseado na região selecionada
      this.financialTransactions.RegionId = this.currentSelectedRegion?.id;
      // Adicionar campo Confirmed (será definido pela regra de negócio)
      this.financialTransactions.confirmed = false;

      // Inicializar campos de descarga
      this.financialTransactions.descarga = false;
      this.financialTransactions.regionIdDestino = null;

      // Para cobradores, forçar que ignoreSeller seja false (não podem criar lançamentos da banca)
      this.ignoreSeller = this.isCobrador ? false : false;
      this.sellerFormSearch = "";
      this.selectedTab = 2;
    },
    
    editFinancialTransactions(id) {
      if (!this.canCreateEditTransactions) {
        toast.error("Você não tem permissão para editar lançamentos financeiros!");
        return;
      }

      this.selectedTab = 2;
      this.getById(id);
    },
    
    reset() {
      const _resetNow = new Date();
      const _resetDateStr = `${_resetNow.getFullYear()}-${String(_resetNow.getMonth() + 1).padStart(2, '0')}-${String(_resetNow.getDate()).padStart(2, '0')}`;
      this.financialTransactions = {};
      this.financialTransactions.date = _resetDateStr;
      // Manter o usuário logado ao resetar
      this.financialTransactions.userId = this.currentUser?.id;
      // Adicionar o RegionId obrigatório baseado na região selecionada
      this.financialTransactions.RegionId = this.currentSelectedRegion?.id;
      // Adicionar campo Confirmed (será definido pela regra de negócio)
      this.financialTransactions.confirmed = false;
      
      // Inicializar campos de descarga
      this.financialTransactions.descarga = false;
      this.financialTransactions.regionIdDestino = null;
      
      this.ignoreSeller = false;
      this.sellerFormSearch = "";
      this.selectedTab = 1;
    },
    
    validateFinancialTransactions() {
      // Verificar RegionId primeiro e tentar corrigir se possível
      if (this.financialTransactions.RegionId == null || this.financialTransactions.RegionId === undefined) {
        if (this.currentSelectedRegion?.id) {
          this.financialTransactions.RegionId = this.currentSelectedRegion.id;
        } else {
          toast.error("É necessário selecionar uma região ativa para criar lançamentos financeiros!");
          return false;
        }
      }
      
      // Verificar se cobrador está tentando criar lançamento da banca
      if (this.isCobrador && this.ignoreSeller) {
        toast.error("Você não tem permissão para criar lançamentos da banca!");
        return false;
      }
      
      if (this.financialTransactions.description == null || this.financialTransactions.description == "") {
        toast.error("O campo Descrição é obrigatório!");
        return false;
      }
      
      if (this.financialTransactions.sellerId == null && this.ignoreSeller == false) {
        toast.error("Selecione um vendedor ou marque como lançamento da banca!");
        return false;
      }
      
      if (this.financialTransactions.date == null || this.financialTransactions.date == "") {
        toast.error("O campo Data é obrigatório!");
        return false;
      }
      
      if (this.financialTransactions.type == null || this.financialTransactions.type == "") {
        toast.error("O campo Tipo é obrigatório!");
        return false;
      }
      
      if (this.financialTransactions.value == null || this.financialTransactions.value === "") {
        toast.error("O campo Valor é obrigatório!");
        return false;
      }

      if (
        !this.ignoreSeller &&
        this.financialTransactions.sellerId &&
        !this.financialTransactions.id &&
        this.financialTransactions.type === this.TransactionType.ENTRADA
      ) {
        const due = this.financialTransactions.amountDueBefore;
        if (due === null || due === undefined || due === "") {
          toast.error("Informe o saldo anterior do vendedor!");
          return false;
        }
      }
      
      // Validar região destino se for descarga
      if (this.shouldShowRegionDestino) {
        if (!this.financialTransactions.regionIdDestino) {
          toast.error("Para lançamentos de descarga, é obrigatório selecionar a região de destino!");
          return false;
        }
      }
      
      // Definir status de confirmação baseado no tipo de lançamento
      this.setConfirmationStatus();
      
      return true;
    },

    setConfirmationStatus() {
      // Se é um lançamento de descarga, deve ficar como pendente
      if (this.financialTransactions.descarga) {
        this.financialTransactions.confirmed = false;
      } else {
        // Se não é descarga (lançamento normal para banca ou vendedor), deve ser confirmado automaticamente
        this.financialTransactions.confirmed = true;
      }
    },
    
    searchFinancialTransactions(p) {
      this.pageIndex = p;
      const isAllSellers = !this.clearSellerSearch && this.selectedSeller === 'all';

      if (!this.clearSellerSearch && !isAllSellers && (!this.selectedSeller || this.selectedSeller.length === 0)){
        this.loading = false;
        toast.warning("Selecione um Vendedor!");
        return;
      }
      if (this.financialTransactionsDateBegin == null || this.financialTransactionsDateBegin == "") {
        this.loading = false;
        toast.warning("Selecione a Data Inicial!");
        return;
      }
      if (this.financialTransactionsDateEnd == null || this.financialTransactionsDateEnd == "") {
        this.loading = false;
        toast.warning("Selecione a Data Final!");
        return;
      }
      
      this.loading = true;
      
      // Banca / Todos: sem sellerId; vendedor específico: usa o selecionado
      const sellerId = this.clearSellerSearch || isAllSellers ? null : (this.selectedSeller || null);
      const sellerName = this.clearSellerSearch
        ? 'Banca'
        : (isAllSellers
          ? 'Todos'
          : (this.availableSellers.find(c => c.id === this.selectedSeller)?.name || ''));
      
      let request = {
        sellerId: sellerId,
        seller: sellerName,
        financialTransactionsDateBegin: this.financialTransactionsDateBegin + 'T00:00:00',
        financialTransactionsDateEnd: this.financialTransactionsDateEnd + 'T23:59:59',
        pageIndex: this.pageIndex - 1
      };

      // Adicionar filtros de região para lançamentos da banca ou "Todos"
      if (this.clearSellerSearch) {
        // Para lançamentos da banca, aplicar filtro de região se disponível
        if (this.currentSelectedRegion) {
          request.regionId = this.currentSelectedRegion.id;
        }
        
        // Adicionar parâmetro para indicar que queremos apenas lançamentos da banca
        request.onlyBancaTransactions = true;
      } else if (isAllSellers) {
        if (this.currentSelectedRegion) {
          request.regionId = this.currentSelectedRegion.id;
        }
        request.allTransactions = true;
      }

      // Adicionar filtro de tipo de transação se selecionado
      if (this.selectedTransactionType) {
        request.type = this.selectedTransactionType;
      }

      if (this.selectedCollectorId) {
        request.collectorId = this.selectedCollectorId;
      }

      this.selectedSellerName = request.seller || 'Banca';


      mainService
        .searchFinancialTransactions(request)
        .then((response) => {
          
          this.sellerBalance = response.data.result;
          
          this.pages = [];
          let pages = response.data.totalRows / 10;
          if (pages % 1 !== 0)
            pages++
          for (let i = 1; i <= pages; i++)
            this.pages.push(i)
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    async searchSellers() {
      try {
        const response = await mainService.searchSellers();
        
        this.sellers = (response.data || []).map(seller => {
          if (!seller) return null;
          
          // Buscar a região do vendedor para obter a banca
          const sellerRegion = (this.regions && Array.isArray(this.regions)) ? 
            this.regions.find(r => r && r.id === seller.regionId) : null;
          const sellerBanca = (sellerRegion && this.bancas && Array.isArray(this.bancas)) ? 
            this.bancas.find(b => b && b.id === sellerRegion.bancaId) : null;
          
          return {
            ...seller,
            bancaId: sellerBanca?.id || null, // ID da banca baseado na região
            bancaName: sellerBanca?.name || '-', // Nome da banca baseado na região
          };
        }).filter(seller => seller !== null);
        
      } catch (error) {
        console.error('Erro ao consultar vendedores:', error);
        toast.error("Erro ao consultar os Vendedores!");
        this.sellers = [];
      }
    },

    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar bancas:', error);
      }
    },

    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar regiões:', error);
      }
    },

    async loadRegionsForDescarga() {
      try {
        if (!this.currentSelectedRegion?.id) {
          this.regionsForDescarga = [];
          return;
        }

        // Se a região atual é uma região de descarga (OnlyDescarga = true)
        if (this.currentSelectedRegion.onlyDescarga) {
          const availableRegions = [];
          
          // 1. Mostrar regiões normais que enviam descarga para esta região de descarga
          const normalRegions = (this.regions || []).filter(region => 
            region && 
            region.id !== this.currentSelectedRegion.id && 
            !region.onlyDescarga &&
            region.descargaRegionId === this.currentSelectedRegion.id
          );
          availableRegions.push(...normalRegions);
          
          // 2. Mostrar outras regiões de descarga que enviam para esta região (redescarga recebe de descarga)
          // Quando uma região de descarga tem descargaRegionId apontando para a região atual, ela envia para ela
          const descargaRegions = (this.regions || []).filter(region => 
            region && 
            region.id !== this.currentSelectedRegion.id && 
            region.onlyDescarga &&
            region.descargaRegionId === this.currentSelectedRegion.id
          );
          availableRegions.push(...descargaRegions);
          
          // 3. Mostrar a região de descarga/redescarga que esta região envia (se houver)
          // Quando uma região de descarga tem descargaRegionId, ela envia para outra região (pode ser redescarga)
          if (this.currentSelectedRegion.descargaRegionId) {
            const targetRegion = (this.regions || []).find(region => 
              region && region.id === this.currentSelectedRegion.descargaRegionId
            );
            if (targetRegion && !availableRegions.find(r => r.id === targetRegion.id)) {
              availableRegions.push(targetRegion);
            }
          }
          
          this.regionsForDescarga = availableRegions;
        } else {
          // Se a região atual NÃO é de descarga, mostrar apenas a região específica do DescargaRegionId
          if (this.currentSelectedRegion.descargaRegionId) {
            const targetRegion = (this.regions || []).find(region => 
              region && region.id === this.currentSelectedRegion.descargaRegionId
            );
            this.regionsForDescarga = targetRegion ? [targetRegion] : [];
          } else {
            this.regionsForDescarga = [];
          }
        }
      } catch (error) {
        console.error('Erro ao carregar regiões para descarga:', error);
        this.regionsForDescarga = [];
      }
    },
    
    getById(id) {
      this.loading = true;
      mainService
        .getFinancialTransactionsById(id)
        .then((response) => {
          this.financialTransactions = response.data;
          this.financialTransactions.date = this.setDateInput(this.financialTransactions.date);
          
          // Garantir que o RegionId seja mantido (usar o da região ativa se não houver)
          if (!this.financialTransactions.RegionId && this.currentSelectedRegion?.id) {
            this.financialTransactions.RegionId = this.currentSelectedRegion.id;
          }
          
          // Garantir que confirmed seja um boolean
          if (this.financialTransactions.confirmed === undefined || this.financialTransactions.confirmed === null) {
            this.financialTransactions.confirmed = false;
          }
          
          // Configurar descarga baseado na existência de RegionIdDestino (vem do backend em PascalCase)
          // Se tem RegionIdDestino definido, é uma transação de descarga
          this.financialTransactions.descarga = !!(this.financialTransactions.RegionIdDestino && this.financialTransactions.RegionIdDestino !== null);
          
          // Mapear RegionIdDestino para regionIdDestino (frontend usa camelCase)
          this.financialTransactions.regionIdDestino = this.financialTransactions.RegionIdDestino;
          
          if(!this.financialTransactions.sellerId) {
            this.ignoreSeller = true;
            this.sellerFormSearch = "";
            
            // Verificar se cobrador está tentando editar lançamento da banca
            if (this.isCobrador) {
              toast.error("Você não tem permissão para editar lançamentos da banca!");
              this.reset();
              return;
            }
          } else {
            this.ignoreSeller = false;
            const seller = this.sellers.find(
              (s) =>
                String(s.id) === String(this.financialTransactions.sellerId),
            );
            this.sellerFormSearch = seller?.name || "";
          }
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    async insertFinancialTransactions() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditTransactions) {
          toast.error("Você não tem permissão para criar lançamentos financeiros!");
          return;
        }

        if (!this.validateFinancialTransactions()) {
          return;
        }

        // Garantir que o userId está sendo enviado
        if (!this.financialTransactions.userId) {
          this.financialTransactions.userId = this.currentUser?.id;
        }

        // Enviar data no horário local atual (backend trata como horário local)
        const now = new Date();
        const currentTime = String(now.getHours()).padStart(2, '0') + ':' +
                           String(now.getMinutes()).padStart(2, '0') + ':' +
                           String(now.getSeconds()).padStart(2, '0');

        const transactionData = {
          ...this.financialTransactions,
          date: this.financialTransactions.date + 'T' + currentTime,
          Confirmed: this.financialTransactions.confirmed,  // Usar o valor definido pela regra de negócio
          RegionIdDestino: this.financialTransactions.regionIdDestino,  // Mapear para PascalCase para o backend
          amountDueBefore:
            !this.ignoreSeller &&
            this.financialTransactions.sellerId &&
            this.financialTransactions.type === this.TransactionType.ENTRADA &&
            this.financialTransactions.amountDueBefore !== null &&
            this.financialTransactions.amountDueBefore !== undefined &&
            this.financialTransactions.amountDueBefore !== ""
              ? Number(this.financialTransactions.amountDueBefore)
              : null,
        };

        await mainService.insertFinancialTransactions(transactionData);
        toast.success("Lançamento cadastrado com sucesso!");
        this.reset();
        this.refreshListingAfterTransaction(transactionData);
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    
    async updateFinancialTransactions() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditTransactions) {
          toast.error("Você não tem permissão para editar lançamentos financeiros!");
          return;
        }

        if (!this.validateFinancialTransactions()) {
          return;
        }

        const ft = this.financialTransactions;
        const id = ft.id ?? ft.Id;
        if (!id) {
          toast.error("ID do lançamento não encontrado. Recarregue e tente novamente.");
          return;
        }

        const now = new Date();
        const currentTime = String(now.getHours()).padStart(2, '0') + ':' +
                           String(now.getMinutes()).padStart(2, '0') + ':' +
                           String(now.getSeconds()).padStart(2, '0');
        const dateStr = (ft.date && !ft.date.includes('T')) ? (ft.date + 'T' + currentTime) : ft.date;

        // Payload explícito no formato esperado pelo backend (FinancialTransactionsUpdateRequest)
        const transactionData = {
          Id: id,
          SellerId: this.ignoreSeller ? null : (ft.sellerId ?? ft.SellerId),
          RegionId: ft.regionId ?? ft.RegionId,
          Date: dateStr,
          Description: ft.description ?? ft.Description ?? '',
          Type: ft.type ?? ft.Type,
          Value: Number(ft.value ?? ft.Value ?? 0),
          RegionIdDestino: ft.regionIdDestino ?? ft.RegionIdDestino ?? null,
          Confirmed: ft.confirmed ?? ft.Confirmed ?? false,
          UserId: ft.userId ?? ft.UserId ?? this.currentUser?.id ?? null
        };
        transactionData.sellerId = transactionData.SellerId; // para refreshListingAfterTransaction

        await mainService.updateFinancialTransactions(transactionData);
        toast.success("Lançamento atualizado com sucesso!");
        this.reset();
        this.refreshListingAfterTransaction(transactionData);
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    
    refreshListingAfterTransaction(transactionData) {
      // Atualizar os filtros baseado no lançamento que foi criado/editado
      if (!transactionData.sellerId) {
        // Se não tem sellerId, é um lançamento da banca
        this.clearSellerSearch = true;
        this.selectedSeller = null;
        this.selectedSellerName = "Banca";
        this.sellerFilterSearch = "";
      } else {
        // Se tem sellerId, configurar o filtro para o vendedor específico
        this.clearSellerSearch = false;
        this.selectedSeller = transactionData.sellerId;
        // Buscar o nome do vendedor
        const seller = this.availableSellers.find(s => s.id === transactionData.sellerId)
          || this.sellers.find(s => String(s.id) === String(transactionData.sellerId));
        this.selectedSellerName = seller ? seller.name : "";
        this.sellerFilterSearch = seller ? seller.name : "";
      }
      
      // Atualizar as datas para incluir a data do lançamento
      const _txNow = new Date();
      const transactionDate = `${_txNow.getFullYear()}-${String(_txNow.getMonth() + 1).padStart(2, '0')}-${String(_txNow.getDate()).padStart(2, '0')}`;
      
      // Se a data do lançamento estiver fora do range atual, expandir o range
      if (transactionDate < this.financialTransactionsDateBegin) {
        this.financialTransactionsDateBegin = transactionDate;
      }
      if (transactionDate > this.financialTransactionsDateEnd) {
        this.financialTransactionsDateEnd = transactionDate;
      }
      
      // Recarregar a listagem com os filtros atualizados
      this.searchFinancialTransactions(1);
    },
    
    deleteFinancialTransactions(idFinancialTransactions) {
      if (!this.canCreateEditTransactions) {
        toast.error("Você não tem permissão para excluir lançamentos financeiros!");
        return;
      }

      // Verificar se é um lançamento da banca (sem vendedor)
      const transaction = (this.sellerBalance?.financialTransactions || this.sellerBalance?.FinancialTransactions || [])
        .find(t => t.id === idFinancialTransactions);
      
      if (transaction && !transaction.sellerId && this.isCobrador) {
        toast.error("Você não tem permissão para excluir lançamentos da banca!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão deste lançamento?",
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            this.loading = true;
            mainService
              .deleteFinancialTransactions(idFinancialTransactions)
              .then(() => {
                toast.success("Lançamento excluído com sucesso!");
                this.searchFinancialTransactions(this.pageIndex);
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              })
              .finally(() => {
                this.loading = false;
              });
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0].focus();
      }, 1);
    },
    
    setDateDisplay(value) {
      return jsFunctions.setDateDisplay(value);
    },
    
    setDateTimeDisplay(value) {
      return jsFunctions.setDateTimeDisplay(value);
    },
    
    setCurrencyString(value) {
      value = value == undefined ? 0 : value
      return jsFunctions.setCurrencyString(value);
    },
    
    setDateInput(value) {
      return jsFunctions.setDateInput(value);
    },

    // Método para buscar nome da região por ID
    getRegionNameById(regionId) {
      if (!regionId || !this.regions || !Array.isArray(this.regions)) {
        return null;
      }
      const region = this.regions.find(r => r && r.id === regionId);
      return region ? region.name : null;
    },

    // Método para verificar se um lançamento deve mostrar o botão de aprovação de descarga
    shouldShowDescargaApprovalButton(transaction) {
      // Uma transação é considerada de descarga se tem RegionIdDestino definido
      const regionDestino = transaction.RegionIdDestino || transaction.regionIdDestino;
      if (!regionDestino) {
        return false;
      }
      
      // Verificar se está pendente (não confirmada)
      if (transaction.confirmed) {
        return false;
      }
      
      // ADMIN pode aprovar qualquer descarga
      if (this.isAdmin) {
        return true;
      }
      
      // Verificar se o usuário é responsável pela região destino da descarga
      if (this.userResponsibleRegions && Array.isArray(this.userResponsibleRegions)) {
        return this.userResponsibleRegions.includes(regionDestino);
      }
      
      return false;
    },

    // Método para abrir o modal de aprovação/rejeição de descarga
    openDescargaApprovalModal(transaction) {
      this.selectedDescarga = transaction;
      this.showDescargaApprovalModal = true;
    },

    // Método para fechar o modal de aprovação/rejeição de descarga
    closeDescargaApprovalModal() {
      this.showDescargaApprovalModal = false;
      this.selectedDescarga = null;
    },

    // Método para aprovar a descarga
    async approveDescarga() {
      if (!this.selectedDescarga) return;

      this.descargaActionLoading = true;
      try {
        await mainService.approveDescarga(this.selectedDescarga.id);
        toast.success("Descarga aprovada com sucesso!");
        this.closeDescargaApprovalModal();
        this.searchFinancialTransactions(this.pageIndex);
      } catch (error) {
        console.error('❌ Erro ao aprovar descarga:', error);
        toast.error(handleErrorMessage(error));
      } finally {
        this.descargaActionLoading = false;
      }
    },

    // Método para rejeitar a descarga
    async rejectDescarga() {
      if (!this.selectedDescarga) return;

      this.descargaActionLoading = true;
      try {
        await mainService.rejectDescarga(this.selectedDescarga.id);
        toast.success("Descarga rejeitada com sucesso!");
        this.closeDescargaApprovalModal();
        this.searchFinancialTransactions(this.pageIndex);
      } catch (error) {
        console.error('❌ Erro ao rejeitar descarga:', error);
        toast.error(handleErrorMessage(error));
      } finally {
        this.descargaActionLoading = false;
      }
    },

    printReport() {
      const transactions = this.sellerBalance?.financialTransactions || this.sellerBalance?.FinancialTransactions || [];
      if (!transactions || transactions.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      const printContent = this.generatePrintHTML();
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generatePrintHTML() {
      const transactions = this.sellerBalance?.financialTransactions || this.sellerBalance?.FinancialTransactions || [];
      const sellerName = this.selectedSellerName || (this.clearSellerSearch ? "Banca" : "Todos");
      const visibleColumns = this.getVisibleExportColumns();
      
      let tableRows = "";
      transactions.forEach(transaction => {
        const cells = this.buildExportRow(transaction, true)
          .map((value, index) => {
            const isValue = visibleColumns[index]?.key === "value";
            const style = isValue ? ' style="text-align: right;"' : "";
            return `<td${style}>${value}</td>`;
          })
          .join("");
        tableRows += `<tr>${cells}</tr>`;
      });

      const headerRow = visibleColumns.map(col => `<th>${col.label}</th>`).join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Lançamentos Financeiros</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              body { margin: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Lançamentos Financeiros</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.financialTransactionsDateBegin} até ${this.financialTransactionsDateEnd}</p>
            <p><strong>Vendedor:</strong> ${sellerName}</p>
            ${this.selectedTransactionType ? `<p><strong>Tipo:</strong> ${this.selectedTransactionType === 'ENTRADA' ? this.TransactionTypeDisplay.ENTRADA : this.TransactionTypeDisplay.SAIDA}</p>` : ''}
            <p><strong>Data de geração:</strong> ${new Date().toLocaleString("pt-BR")}</p>
          </div>
          <table>
            <thead>
              <tr>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer">
            <p>Total de registros: ${transactions.length}</p>
          </div>
        </body>
        </html>
      `;
    },

    exportToPDF() {
      const transactions = this.sellerBalance?.financialTransactions || this.sellerBalance?.FinancialTransactions || [];
      if (!transactions || transactions.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        
        // Título
        doc.setFontSize(16);
        doc.text("Relatório de Lançamentos Financeiros", 14, 15);
        
        // Informações do relatório
        doc.setFontSize(10);
        doc.text(`Período: ${this.financialTransactionsDateBegin} até ${this.financialTransactionsDateEnd}`, 14, 25);
        const sellerName = this.selectedSellerName || (this.clearSellerSearch ? "Banca" : "Todos");
        doc.text(`Vendedor: ${sellerName}`, 14, 30);
        if (this.selectedTransactionType) {
          doc.text(`Tipo: ${this.selectedTransactionType === 'ENTRADA' ? this.TransactionTypeDisplay.ENTRADA : this.TransactionTypeDisplay.SAIDA}`, 14, 35);
        }
        doc.text(`Data de geração: ${new Date().toLocaleString("pt-BR")}`, 14, 40);
        
        const tableColumns = this.getVisibleExportColumns().map(col =>
          col.key === "createdAt" ? "Data/Hora" : col.label,
        );
        const tableData = transactions.map(transaction =>
          this.buildExportRow(transaction, true),
        );
        
        // Adicionar tabela usando autoTable diretamente
        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          startY: 45,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap"
        });
        
        // Salvar PDF
        const fileName = `Lancamentos_Financeiros_${this.financialTransactionsDateBegin}_${this.financialTransactionsDateEnd}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      const transactions = this.sellerBalance?.financialTransactions || this.sellerBalance?.FinancialTransactions || [];
      if (!transactions || transactions.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const headers = this.getVisibleExportColumns().map(col => col.label);
        const tableData = transactions.map(transaction =>
          this.buildExportRow(transaction, false),
        );
        
        // Criar workbook
        const wb = XLSX.utils.book_new();
        
        // Preparar dados completos: informações + cabeçalho + dados
        const sellerName = this.selectedSellerName || (this.clearSellerSearch ? "Banca" : "Todos");
        const allData = [
          ["Relatório de Lançamentos Financeiros"],
          [""],
          ["Período:", `${this.financialTransactionsDateBegin} até ${this.financialTransactionsDateEnd}`],
          ["Vendedor:", sellerName],
        ];
        
        if (this.selectedTransactionType) {
          allData.push(["Tipo:", this.selectedTransactionType === 'ENTRADA' ? this.TransactionTypeDisplay.ENTRADA : this.TransactionTypeDisplay.SAIDA]);
        }
        
        allData.push(
          ["Data de geração:", new Date().toLocaleString("pt-BR")],
          [""],
          headers, // Cabeçalho da tabela
          ...tableData // Dados da tabela
        );
        
        // Criar worksheet com todos os dados
        const ws = XLSX.utils.aoa_to_sheet(allData);
        
        // Definir largura das colunas
        const colWidths = headers.map(() => ({ wch: 15 }));
        ws["!cols"] = colWidths;
        
        // Adicionar worksheet ao workbook
        XLSX.utils.book_append_sheet(wb, ws, "Lançamentos Financeiros");
        
        // Salvar arquivo
        const fileName = `Lancamentos_Financeiros_${this.financialTransactionsDateBegin}_${this.financialTransactionsDateEnd}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar Excel:", error);
        toast.error("Erro ao exportar Excel");
      }
    }
  },
};
</script>
